import {
  Box,
  Typography,
  Stack,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  LinearProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ReceiptLongRounded,
  CheckCircleRounded,
  WarningRounded,
  HourglassTopRounded,
  CancelRounded,
  UploadFileRounded,
  VisibilityRounded,
  PaymentsOutlined,
  SwapHorizRounded,
  DeleteOutlineRounded,
  InfoOutlined,
} from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import { tuitionApi, PaymentSummary } from '../../../lib/api/tuition.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { SubmitPaymentDialog } from './SubmitPaymentDialog';
import { PaymentInfoDialog } from './PaymentInfoDialog';
import { formatMoney } from '../../../lib/i18n-format';

export interface TuitionSummaryCardProps {
  studentId: string;
  /** When true, admin-level controls are shown (e.g., delete any payment). */
  isAdmin?: boolean;
  /** Called after the student successfully deletes their payment plan */
  onPlanDeleted?: () => void;
}

const statusColor = (status: string) => {
  switch (status) {
    case 'verified':
      return 'success';
    case 'pending_verification':
      return 'warning';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'verified':
      return <CheckCircleRounded fontSize="small" />;
    case 'pending_verification':
      return <HourglassTopRounded fontSize="small" />;
    case 'rejected':
      return <CancelRounded fontSize="small" />;
    default:
      return null;
  }
};

/** Las tintas del portal (fondo siempre oscuro, en los dos temas de la app), las
 *  mismas de sus páginas: clara para títulos y montos, gris para lo secundario. */
const PORTAL_INK = '#e6edf3';
const PORTAL_MUTED = '#8b949e';

export function TuitionSummaryCard({ studentId, isAdmin, onPlanDeleted }: TuitionSummaryCardProps) {
  // Tintas de la tarjeta. En la ficha, las del tema (claro u oscuro). En el
  // portal, que pinta siempre su fondo oscuro, las suyas: con las del tema, en
  // modo claro, quedarían oscuras sobre oscuro. Antes era `white` fijo para
  // títulos (invisible en la ficha con el tema claro) y `text.secondary` para
  // Total, Pagado y Vence (invisible en el portal con el tema claro).
  const ink = isAdmin ? 'text.primary' : PORTAL_INK;
  const muted = isAdmin ? 'text.secondary' : PORTAL_MUTED;
  const { t } = useTranslation('sis');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentTenantId } = useTenantStore();
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [payInfoOpen, setPayInfoOpen] = useState(false);

  // Change plan state
  const [canChangePlan, setCanChangePlan] = useState(false);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [newPlanOption, setNewPlanOption] = useState<'3-months' | '6-months' | '9-months' | 'full'>(
    '9-months',
  );
  const [changingPlan, setChangingPlan] = useState(false);
  const [changePlanError, setChangePlanError] = useState<string | null>(null);

  // Delete plan state
  const [deletePlanOpen, setDeletePlanOpen] = useState(false);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [deletePlanError, setDeletePlanError] = useState<string | null>(null);

  // Delete payment state
  const [deletePaymentId, setDeletePaymentId] = useState<string | null>(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [deletePaymentError, setDeletePaymentError] = useState<string | null>(null);

  const load = async () => {
    if (!currentTenantId) return;
    setLoading(true);
    setError(null);
    try {
      // 1. Find active tuition plan for this student
      const plansQuery = query(
        collection(db, `tenants/${currentTenantId}/tuitionPlans`),
        where('studentId', '==', studentId),
        where('status', '==', 'active'),
        limit(1),
      );
      const plansSnap = await getDocs(plansQuery);

      if (plansSnap.empty) {
        // No active plan — check for any plan (completed, overdue, etc.)
        const anyPlanQuery = query(
          collection(db, `tenants/${currentTenantId}/tuitionPlans`),
          where('studentId', '==', studentId),
          limit(1),
        );
        const anySnap = await getDocs(anyPlanQuery);
        const fallbackDocs = anySnap.docs;
        if (fallbackDocs.length === 0) {
          setSummary(null);
          return;
        }
        const fallbackDoc = fallbackDocs[0]!;
        const fallbackData = fallbackDoc.data();
        setActivePlanId(fallbackDoc.id);
        const data = await tuitionApi.getStudentPaymentSummary({
          tenantId: currentTenantId,
          studentId,
          termId: fallbackData.termId ?? '',
        });
        setSummary(data);
        return;
      }

      const planDoc = plansSnap.docs[0]!;
      const planData = planDoc.data();
      setActivePlanId(planDoc.id);

      // 2. Fetch payment summary from cloud function
      const data = await tuitionApi.getStudentPaymentSummary({
        tenantId: currentTenantId,
        studentId,
        termId: planData.termId ?? '',
      });
      setSummary(data);

      // 3. Check if student can change plan (no verified/pending payments)
      if (data) {
        const hasActivePayments = data.payments.some(
          (p) => p.status === 'verified' || p.status === 'pending_verification',
        );
        setCanChangePlan(!hasActivePayments);
      }
    } catch (e: any) {
      setError(e?.message ?? 'Error loading payment info');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!currentTenantId || !deletePaymentId) return;
    setDeletingPayment(true);
    setDeletePaymentError(null);
    try {
      await tuitionApi.deletePayment({
        tenantId: currentTenantId,
        paymentId: deletePaymentId,
        studentId,
        isAdminOverride: isAdmin ?? false,
      });
      setDeletePaymentId(null);
      load();
    } catch (e: any) {
      setDeletePaymentError(e?.message ?? 'Error al eliminar el pago');
    } finally {
      setDeletingPayment(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentTenantId, studentId]);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={32} />
      </Box>
    );

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!summary)
    return (
      <Box sx={{ textAlign: 'center', py: 8, color: muted }}>
        <PaymentsOutlined sx={{ fontSize: 60, opacity: 0.2, mb: 2 }} />
        <Typography variant="h6">{t('tuition.noActivePlan')}</Typography>
        <Typography variant="body2" color={muted}>
          {t('tuition.noActivePlanDesc')}
        </Typography>
      </Box>
    );

  const paidPercent =
    summary.totalDue > 0 ? Math.round((summary.totalPaid / summary.totalDue) * 100) : 0;

  const currency = summary.plan.currency ?? 'CLP';
  const fmt = (n: number | undefined | null) => formatMoney(n ?? 0, currency);

  // Every payment gets the number it shows under "Pagos recientes", so a cuota
  // can name the payments covering it and the two lists can be read together.
  // Without this the only way to tell which of two same-reference payments to
  // delete was to open each receipt.
  const paymentNumberById = new Map<string, number>();
  summary.payments.forEach((p, idx) => paymentNumberById.set(p.id, idx + 1));

  interface AllocationRef {
    paymentNumber: number;
    amount: number;
    status: string;
  }

  // installmentId → the payments imputed to it. Rejected payments are left out:
  // they carry allocations but no money, and showing them would suggest a cuota
  // is covered when it isn't.
  const allocationsByInstallment = new Map<string, AllocationRef[]>();
  const pendingReceiptByInstallment = new Map<string, string>();
  for (const p of summary.payments) {
    if (p.status === 'rejected') continue;
    for (const alloc of p.allocations ?? []) {
      const refs = allocationsByInstallment.get(alloc.installmentId) ?? [];
      refs.push({
        paymentNumber: paymentNumberById.get(p.id) ?? 0,
        amount: alloc.amount,
        status: p.status,
      });
      allocationsByInstallment.set(alloc.installmentId, refs);

      if (p.status === 'pending_verification' && p.receiptUrl) {
        // First pending receipt wins per cuota — but now it's the receipt of a
        // payment actually imputed there, not whichever one came first.
        if (!pendingReceiptByInstallment.has(alloc.installmentId)) {
          pendingReceiptByInstallment.set(alloc.installmentId, p.receiptUrl);
        }
      }
    }
  }

  return (
    <Box>
      {/* Summary Header */}
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, background: 'rgba(255,255,255,0.02)' }}
      >
        {/* Title row: stacks vertically on mobile */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
          gap={1.5}
          mb={2}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color={ink}>
              {t('tuition.paymentSummary')}
            </Typography>
            <Typography variant="body2" color={muted}>
              {t('tuition.total')}:{' '}
              <Box component="strong" sx={{ color: ink }}>
                {fmt(summary.totalDue)}
              </Box>
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
              {summary.plan.exemptionReason && (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`Eximido: ${summary.plan.exemptionReason}`}
                />
              )}
              {summary.plan.discountAmount && summary.plan.discountAmount > 0 && (
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label={`Descuento: ${fmt(summary.plan.discountAmount)}`}
                />
              )}
              {summary.plan.adminScholarshipGrant && (
                <Chip
                  size="small"
                  color="secondary"
                  variant="outlined"
                  label={
                    (summary.plan.adminScholarshipGrant.source ?? 'manual') !== 'manual' &&
                    summary.plan.adminScholarshipGrant.reason
                      ? `${summary.plan.adminScholarshipGrant.reason} — ${summary.plan.adminScholarshipGrant.tuitionDiscountPercent}%`
                      : t('tuition.chips.scholarshipSummary', {
                          pct: summary.plan.adminScholarshipGrant.tuitionDiscountPercent,
                          reason: summary.plan.adminScholarshipGrant.reason,
                        })
                  }
                />
              )}
            </Stack>
          </Box>

          {/* Action buttons: wrap on small screens */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {canChangePlan &&
              activePlanId &&
              (isMobile ? (
                <Tooltip title="Cambiar Plan">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setChangePlanError(null);
                      setChangePlanOpen(true);
                    }}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <SwapHorizRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<SwapHorizRounded />}
                  onClick={() => {
                    setChangePlanError(null);
                    setChangePlanOpen(true);
                  }}
                  size="small"
                >
                  Cambiar Plan
                </Button>
              ))}
            {canChangePlan &&
              activePlanId &&
              (isMobile ? (
                <Tooltip title="Eliminar Plan">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      setDeletePlanError(null);
                      setDeletePlanOpen(true);
                    }}
                    sx={{ border: '1px solid', borderColor: 'error.dark', borderRadius: 1 }}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineRounded />}
                  onClick={() => {
                    setDeletePlanError(null);
                    setDeletePlanOpen(true);
                  }}
                  size="small"
                >
                  Eliminar Plan
                </Button>
              ))}
            {/* Payment info button */}
            {isMobile ? (
              <Tooltip title={t('tuition.paymentInfo.howToPay')}>
                <IconButton
                  size="small"
                  aria-label={t('tuition.paymentInfo.howToPay')}
                  onClick={() => setPayInfoOpen(true)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.dark',
                    borderRadius: 1,
                    color: 'primary.light',
                  }}
                >
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                variant="outlined"
                startIcon={<InfoOutlined />}
                onClick={() => setPayInfoOpen(true)}
                size="small"
                sx={{ whiteSpace: 'nowrap', color: 'primary.light', borderColor: 'primary.dark' }}
              >
                {t('tuition.paymentInfo.howToPay')}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<UploadFileRounded />}
              onClick={() => setSubmitOpen(true)}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {isMobile ? 'Pago' : t('tuition.registerPayment')}
            </Button>
          </Stack>
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color={muted}>
              {t('tuition.paid')}: {fmt(summary.totalPaid)}
            </Typography>
            <Typography variant="body2" color={muted}>
              {paidPercent}%
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={paidPercent}
            color={summary.overdueCount > 0 ? 'error' : 'success'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          {summary.balance > 0 && (
            <Typography variant="body2" color={muted}>
              {t('tuition.pending')}:{' '}
              <Box component="strong" sx={{ color: ink }}>
                {fmt(summary.balance)}
              </Box>
            </Typography>
          )}
          {summary.overdueCount > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <WarningRounded fontSize="small" color="error" />
              <Typography variant="body2" color="error.main">
                {t('tuition.overdueSummary', {
                  count: summary.overdueCount,
                  amount: fmt(summary.overdueAmount),
                })}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Installments */}
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2, background: 'rgba(255,255,255,0.02)' }}
      >
        <Typography variant="subtitle1" fontWeight={600} color={ink} mb={2}>
          {t('tuition.installments')}
        </Typography>
        <Stack spacing={1}>
          {summary.installmentSummary.map((ins) => {
            const allocs = allocationsByInstallment.get(ins.installmentId) ?? [];
            const allocatedTotal = allocs.reduce((s, a) => s + a.amount, 0);
            // More money imputed here than the cuota owes — usually the same
            // transfer registered twice. Surfacing it is the whole point.
            const overAllocated = allocatedTotal > ins.amount;
            return (
              <Stack
                key={ins.installmentId}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                gap={1}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  background: overAllocated
                    ? 'rgba(255,152,0,0.08)'
                    : ins.status === 'overdue'
                      ? 'rgba(244,67,54,0.06)'
                      : 'transparent',
                  border: '1px solid',
                  borderColor: overAllocated
                    ? 'warning.dark'
                    : ins.status === 'overdue'
                      ? 'error.dark'
                      : 'divider',
                }}
              >
                {/* Left: icon + label */}
                <Stack direction="row" spacing={1.5} alignItems="center" flexGrow={1}>
                  <ReceiptLongRounded
                    fontSize="small"
                    color={ins.status === 'overdue' ? 'error' : 'action'}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={600} color={ink}>
                      {ins.label}
                    </Typography>
                    <Typography variant="caption" color={muted}>
                      {t('tuition.dueDate')}: {ins.dueDate}
                    </Typography>

                    {/* Which payments cover this cuota. The number matches the
                      one shown in "Pagos recientes" below. */}
                    {allocs.length > 0 && (
                      <Stack direction="row" gap={0.5} mt={0.75} flexWrap="wrap" useFlexGap>
                        {allocs.map((a) => (
                          <Chip
                            key={`${a.paymentNumber}-${a.amount}`}
                            size="small"
                            variant="outlined"
                            color={a.status === 'pending_verification' ? 'warning' : 'default'}
                            label={`#${a.paymentNumber} · ${fmt(a.amount)}`}
                            sx={{ height: 20, fontSize: '0.68rem' }}
                          />
                        ))}
                      </Stack>
                    )}
                    {overAllocated && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        display="block"
                        mt={0.5}
                        fontWeight={600}
                      >
                        {t('tuition.overAllocatedHint', {
                          allocated: fmt(allocatedTotal),
                          amount: fmt(ins.amount),
                        })}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                {/* Right: amount + status */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: 'flex-end', sm: 'flex-end' }}
                  width={{ xs: '100%', sm: 'auto' }}
                >
                  <Box textAlign="right">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        ins.paidAmount >= ins.amount
                          ? 'success.main'
                          : ins.status === 'overdue'
                            ? 'error.main'
                            : 'warning.main'
                      }
                    >
                      {fmt(ins.amount)}
                    </Typography>
                    {/* Vencida (aunque tenga pagos parciales): lo dice con texto,
                        no solo con el color. Regla de `installmentState`. */}
                    {ins.status === 'overdue' ? (
                      <Typography variant="caption" color="error.main" display="block">
                        {ins.paidAmount > 0
                          ? t('tuition.overduePaid', {
                              paid: fmt(ins.paidAmount),
                              amount: fmt(ins.amount),
                            })
                          : t('tuition.overdueInstallment')}
                      </Typography>
                    ) : (
                      ins.paidAmount > 0 && (
                        <Typography variant="caption" color="success.main">
                          {t('tuition.paid')}: {fmt(ins.paidAmount)}
                        </Typography>
                      )
                    )}
                    {ins.pendingAmount > 0 &&
                      ins.paidAmount < ins.amount &&
                      (() => {
                        const receiptUrl = pendingReceiptByInstallment.get(ins.installmentId);
                        return receiptUrl ? (
                          <Chip
                            component="a"
                            href={receiptUrl}
                            target="_blank"
                            rel="noopener"
                            clickable
                            size="small"
                            icon={<HourglassTopRounded />}
                            deleteIcon={<VisibilityRounded />}
                            onDelete={() => window.open(receiptUrl, '_blank')}
                            label={
                              isMobile
                                ? t('tuition.inReview')
                                : `${t('tuition.inReview')}: ${fmt(ins.pendingAmount)}`
                            }
                            color="warning"
                            variant="outlined"
                            sx={{
                              mt: 0.5,
                              height: 28,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              '& .MuiChip-icon': { fontSize: 13 },
                              '& .MuiChip-deleteIcon': { fontSize: 15, ml: 0.5 },
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<HourglassTopRounded />}
                            label={
                              isMobile
                                ? t('tuition.inReview')
                                : `${t('tuition.inReview')}: ${fmt(ins.pendingAmount)}`
                            }
                            color="warning"
                            variant="outlined"
                            sx={{
                              mt: 0.5,
                              height: 28,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              '& .MuiChip-icon': { fontSize: 13 },
                            }}
                          />
                        );
                      })()}
                  </Box>
                  {ins.status === 'overdue' && !ins.pendingAmount && (
                    <Tooltip title={t('tuition.overdue') as string}>
                      <WarningRounded color="error" fontSize="small" />
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      </Paper>

      {/* Recent Payments */}
      {summary.payments.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, background: 'rgba(255,255,255,0.02)' }}
        >
          <Typography variant="subtitle1" fontWeight={600} color={ink} mb={2}>
            {t('tuition.recentPayments')}
          </Typography>
          <Stack spacing={1} divider={<Divider />}>
            {summary.payments.map((p) => (
              <Stack
                key={p.id}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={0.5}
                py={0.5}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {/* Same number the cuotas above use to point at this payment. */}
                  <Typography
                    variant="caption"
                    color={muted}
                    fontWeight={700}
                    sx={{ minWidth: 24 }}
                  >
                    #{paymentNumberById.get(p.id)}
                  </Typography>
                  {statusIcon(p.status)}
                  <Box>
                    <Typography variant="body2" fontWeight={500} color={ink}>
                      {fmt(p.amount)} — {p.reference}
                    </Typography>
                    <Typography variant="caption" color={muted}>
                      {p.paymentDate}
                      {p.rejectionReason && ` · ${p.rejectionReason}`}
                      {(p.allocations?.length ?? 0) > 0 &&
                        ` · ${t('tuition.appliedTo')}: ${p
                          .allocations!.map(
                            (a) =>
                              summary.installmentSummary.find(
                                (i) => i.installmentId === a.installmentId,
                              )?.label ?? a.installmentId,
                          )
                          .join(', ')}`}
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent={{ xs: 'flex-end', sm: 'flex-end' }}
                  width={{ xs: '100%', sm: 'auto' }}
                >
                  <Chip
                    size="small"
                    label={String(t(`tuition.paymentStatus.${p.status}`, p.status))}
                    color={statusColor(p.status) as any}
                    variant="outlined"
                  />
                  {p.receiptUrl && (
                    <Tooltip title={t('tuition.viewReceipt') as string}>
                      <IconButton size="small" href={p.receiptUrl} target="_blank" rel="noopener">
                        <VisibilityRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {/* Delete button: admin sees it on all payments; students only on pending_verification */}
                  {(isAdmin || p.status === 'pending_verification') && (
                    <Tooltip
                      title={t(
                        isAdmin ? 'tuition.deletePaymentAdmin' : 'tuition.cancelPendingPayment',
                      )}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setDeletePaymentId(p.id)}
                        sx={{ color: 'error.main', opacity: 0.7, ':hover': { opacity: 1 } }}
                      >
                        <DeleteOutlineRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <SubmitPaymentDialog
        open={submitOpen}
        onClose={() => {
          setSubmitOpen(false);
          load();
        }}
        tuitionPlanId={activePlanId ?? ''}
        studentId={studentId}
        currency={summary.plan.currency ?? 'CLP'}
        installments={summary.installmentSummary.map((ins) => ({
          id: ins.installmentId,
          label: ins.label,
          dueDate: ins.dueDate,
          amount: ins.amount,
          type:
            summary.plan.installments.find((i) => i.id === ins.installmentId)?.type ?? 'monthly',
          // A receipt awaiting verification reserves its cuota — the server
          // rejects a second payment on it, so don't offer it here either.
          balance: Math.max(0, ins.amount - ins.paidAmount - ins.pendingAmount),
          pendingAmount: ins.pendingAmount,
        }))}
      />

      {/* Delete Payment Confirmation Dialog */}
      <Dialog
        open={!!deletePaymentId}
        onClose={() => !deletingPayment && setDeletePaymentId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>¿Cancelar este pago?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {deletePaymentError && <Alert severity="error">{deletePaymentError}</Alert>}
            <Typography variant="body2" color={muted}>
              Este pago está pendiente de verificación. Al eliminarlo podrás registrar un nuevo
              comprobante corregido. Esta acción no se puede deshacer.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePaymentId(null)} disabled={deletingPayment}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeletePayment}
            disabled={deletingPayment}
            startIcon={deletingPayment ? <CircularProgress size={16} /> : <DeleteOutlineRounded />}
          >
            Sí, eliminar pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Cómo pagar por transferencia (SEC-3: datos del seminario) ── */}
      <PaymentInfoDialog
        open={payInfoOpen}
        tenantId={currentTenantId}
        onClose={() => setPayInfoOpen(false)}
        onRegister={() => {
          setPayInfoOpen(false);
          setSubmitOpen(true);
        }}
      />

      {/* Change Payment Plan Dialog */}
      <Dialog
        open={changePlanOpen}
        onClose={() => setChangePlanOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cambiar Plan de Pago</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {changePlanError && <Alert severity="error">{changePlanError}</Alert>}
            <Alert severity="info">
              Puedes cambiar tu plan de cuotas mientras no hayas registrado pagos verificados o en
              revisión.
            </Alert>
            <FormControl fullWidth>
              <InputLabel id="new-plan-label">Nuevo plan de cuotas</InputLabel>
              <Select
                labelId="new-plan-label"
                value={newPlanOption}
                label="Nuevo plan de cuotas"
                onChange={(e) => setNewPlanOption(e.target.value as any)}
              >
                <MenuItem value="3-months">3 cuotas mensuales</MenuItem>
                <MenuItem value="6-months">6 cuotas mensuales</MenuItem>
                <MenuItem value="9-months">9 cuotas mensuales</MenuItem>
                <MenuItem value="full">Pago total (sin cuotas)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePlanOpen(false)} disabled={changingPlan}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={changingPlan || !currentTenantId || !activePlanId}
            startIcon={changingPlan ? <CircularProgress size={16} /> : <SwapHorizRounded />}
            onClick={async () => {
              if (!currentTenantId || !activePlanId) return;
              setChangingPlan(true);
              setChangePlanError(null);
              try {
                await tuitionApi.changePaymentPlan({
                  tenantId: currentTenantId,
                  planId: activePlanId,
                  newPaymentPlanOption: newPlanOption,
                  studentId,
                });
                setChangePlanOpen(false);
                await load();
              } catch (e: any) {
                setChangePlanError(e?.message ?? 'Error al cambiar el plan');
              } finally {
                setChangingPlan(false);
              }
            }}
          >
            Confirmar cambio
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Payment Plan Dialog */}
      <Dialog
        open={deletePlanOpen}
        onClose={() => setDeletePlanOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>Eliminar Plan de Pago</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {deletePlanError && <Alert severity="error">{deletePlanError}</Alert>}
            <Alert severity="warning">
              Esta acción eliminará tu plan de pagos actual. Podrás matricularte nuevamente con un
              plan diferente. Solo es posible si <strong>no tienes pagos registrados</strong>.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanOpen(false)} disabled={deletingPlan}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingPlan || !currentTenantId || !activePlanId}
            startIcon={deletingPlan ? <CircularProgress size={16} /> : <DeleteOutlineRounded />}
            onClick={async () => {
              if (!currentTenantId || !activePlanId) return;
              setDeletingPlan(true);
              setDeletePlanError(null);
              try {
                await tuitionApi.deleteTuitionPlan({
                  tenantId: currentTenantId,
                  planId: activePlanId,
                });
                setDeletePlanOpen(false);
                onPlanDeleted?.();
              } catch (e: any) {
                setDeletePlanError(e?.message ?? 'Error al eliminar el plan');
              } finally {
                setDeletingPlan(false);
              }
            }}
          >
            Eliminar Plan
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
